/* eslint-disable no-unused-vars, default-case, no-param-reassign, react/react-in-jsx-scope, react/jsx-closing-bracket-location, no-new */
import * as API from 'es6/core/api';
import AggregateMemory from 'es6/models/aggregate-memory';
import AudioPanel from 'es6/components/apps/settings/audio-panel';
import BaseApp from 'es6/controllers/apps/base-app';
import Memory from 'es6/models/memory';
import { checkAllOverrides, checkForcedAchievement } from 'es6/controllers/apps/debug-overrides';
import Config from 'es6/core/config';
import KanaPanel from 'es6/components/apps/settings/kana-panel';
import * as Loaders from 'es6/core/loaders';
import Logger from 'es6/services/logger';
import { GoalSource, ItemSource, SessionConfig } from 'es6/models/session-config';
import Quizzes from 'es6/controllers/apps/quizzes';
import SettingsModal from 'es6/components/apps/settings/settings-modal';
import * as StudyDataManager from 'es6/services/study-data-manager';
import { t } from 'es6/services/localization';
import SessionProgressBar from 'es6/components/apps/session-progress-bar';

/**
 * The base study app is an extension of the base app. It is used
 * by all the item study quiz types. When extending this class, the
 * following methods need to be overwritten or an error will be tossed:
 *
 * _loadSessionAssets()
 * _showSummaryScreen()
 * _showWelcomeScreen()
 * _updateSessionConfig()
 */
export default class BaseStudyApp extends BaseApp {
  /**
   * Constructor for the base app.
   *
   * @param {String} applicationType   application type for the API
   * @param {Object} appSettings       settings and features used for this app instance
   */
  constructor(applicationType, appSettings) {
    super(applicationType);

    // App settings and toggles passed down from the
    // app selector or inheriting applications.
    this._cueLanguage = appSettings.cueLanguage;
    this._goalId = appSettings.goalId;
    this._onAppQuit = appSettings.onAppQuit;
    this._studyAll = !appSettings.goalId;
    this._userSettings = this._setupUserSettings(appSettings.userSettings);
    this._siteConfiguration = appSettings.siteConfiguration;

    // Used to determine if assets have completely loaded for item packages.
    this._assetLoader = Promise.resolve(true);

    // Keeps track of the number of sessions started and finished.
    this._finishedSessionsCount = 0;

    // Used to check if we are in the welcome screen.
    this._sessionStarted = false;

    // Session config data, used to maintain which configs are disabled and
    // being used.
    this._sessionConfig = this._createSessionConfig();

    // The data for the current session the user is in.
    this._sessionData = null;

    // Used to store the results from the last updated memories.
    this._updatedMemories = [];

    // Update the global settings.
    this._updateGlobalSettings();

    // Show the loading screen.
    this._screenManager.show('loading');

    // Check for unsaved data and once uploaded, fetch data for the session.
    this._checkUnsentSaveData(this._fetchData.bind(this));
  }

  /**
   * Returns a promise that will trigger when assets for the session
   * have finished loading.
   *
   * @returns {Promise}     should return a thenable object
   */
  _loadSessionAssets() {
    throw new Error('_loadSessionAssets() needs to be defined');
  }

  /**
   * Renders the summary screen.
   *
   * @param {Object[]} presentationResults  presentation results sent to server
   * @param {Object}   sessionResults       session results returned from server
   */
  _showSummaryScreen(presentationResults, sessionResults) {
    throw new Error('_showSummaryScreen() needs to be defined');
  }

  /**
   * Renders the welcome screen.
   */
  _showWelcomeScreen() {
    throw new Error('_showWelcomeScreen() needs to be defined');
  }

  /**
   * Updates the session config properties to determine if there
   * are any conflicts.
   *
   * @returns {Boolean}     true on success, false if new session needs to be fetched
   */
  _updateSessionConfig() {
    throw new Error('_updateSessionConfig() needs to be defined');
  }

  /**
   * Set up generic callbacks on the window and buttons.
   *
   * @override
   */
  _setupEvents() {
    super._setupEvents();

    // Show the error screen if an error gets triggered.
    // TODO: Get rid of this legacy stuff.
    Smartfm.Apps.Utilities.ErrorHandling(() => {
      this._screenManager.show('error');
    });
  }

  /**
   * On top of the base app screen, achievements and premium account are needed
   * for regular study apps.
   */
  _setupScreens() {
    super._setupScreens();

    this._screenManager.addScreen('achievements', Smartfm.Apps.Common.Screens.Achievements);

    // For paywall
    this._screenManager.addScreen('premium', Smartfm.Apps.Common.Screens.Premium);
  }

  /**
   * Apply app specific defaults to the user settings object.
   *
   * @params {UserSettings} userSettings
   * @returns {UserSettings}
   */
  _setupUserSettings(userSettings) {
    const appKey = this._getApplicationTypeKey();
    const defaultSessionConfig = this._getDefaultSessionConfig();
    const settings = userSettings.apps[appKey];

    _.defaults(settings.quizOptions, this._getDefaultQuizOptions());
    _.defaults(settings.sessionConfig.course, defaultSessionConfig.course);
    _.defaults(settings.sessionConfig.studyAll, defaultSessionConfig.studyAll);

    return userSettings;
  }

  /**
   * Handles the data necessary to start a session. If more data
   * is necessary before starting an application, override or
   * change _fetchSessionBasedData().
   */
  _fetchData() {
    // Reset _sessionStarted when we load new session data.
    this._sessionStarted = false;

    // If there are any active loaders, stop and refetch data.
    this._abortActiveLoaders();

    // Set the app container to a loading state.
    this._loading(true);

    // Instantiate study loader.
    const studyLoader = this._getStudyLoader();

    // Wait for all the study loader to finish.
    Promise.all([this._addActiveLoader(studyLoader), this._setupStaticAssets()])

    // Set up session data and retrieve more data based on the API results.
      .then(loaderResponse => {
        this._unpackData(loaderResponse[0]);
        return this._fetchSessionBasedData();
      })

    // Show welcome screen.
      .then(() => {
        this._loading(false);
        this._showWelcomeScreen();
      })

      .catch(error => this._errorHandler(error));
  }

  /**
   * Creates a session config.
   *
   * @returns {SessionConfig}
   */
  _createSessionConfig() {
    const appKey = this._getApplicationTypeKey();
    const studyModeKey = this._getStudyModeKey();
    const sessionConfig = this._userSettings.apps[appKey].sessionConfig[studyModeKey];

    return new SessionConfig(sessionConfig);
  }

  /**
   * Executed after unpacking data and before showing the welcome screen.
   * Sets up data that requires session data.
   *
   * @returns {Promise}
   */
  _fetchSessionBasedData() {
    return this._setupMouseoverDictionary(this._sessionData.goals);
  }

  /**
   * Gets default quiz options for the app.
   *
   * @returns {Object}
   */
  _getDefaultQuizOptions() {
    return {};
  }

  /**
   * Gets default session configs for the app.
   *
   * @returns {Object}
   */
  _getDefaultSessionConfig() {
    return {
      course: {
        sessionSize: 10,
        itemSource: ItemSource.ALL,
      },
      studyAll: {
        sessionSize: 10,
        itemSource: ItemSource.ALL,
        goalSource: GoalSource.ALL,
      },
    };
  }

  /**
   * Helper method for looking up application based settings in the
   * user settings object.
   *
   * @returns {String}
   */
  _getApplicationTypeKey() {
    return this._applicationType.replace(/_([a-z])/, x => x[1].toUpperCase());
  }

  /**
   * Helper method for looking up session config settings in the
   * UserSettings object.
   *
   * @returns {String}
   */
  _getStudyModeKey() {
    return this._studyAll ? 'studyAll' : 'course';
  }

  /**
   * Creates the study loader for the session.
   *
   * @returns {Loader}
   */
  _getStudyLoader() {
    if (this._studyAll) {
      return new Loaders.StudyAll(
        this._cueLanguage,
        this._getStudyParameters()
      );
    } else {
      return new Loaders.StudyGoal(
        this._goalId,
        this._getStudyParameters()
      );
    }
  }

  /**
   * Retrieves the session sizes for the app.
   *
   * @returns {Number[]}
   */
  _getSessionSizes() {
    return [5, 10, 20, 50];
  }

  /**
   * Creates study parameters for the application.
   *
   * @returns {Object}
   */
  _getStudyParameters() {
    let sequenceOptions = [];

    if (this._userSettings.isKanaMode() || this._userSettings.isRomajiMode()) {
      // Add sequences options which will exclude:
      // - TransliterationMultipleChoiceLimited
      // - ReverseTransliterationMultipleChoiceLimited
      // - TransliterationFillInTheBlank
      // Which make no sense in these modes. Also exclude:
      // - ReverseMultipleChoiceLimited
      // Which requires a transliteration on the cue and at least 4 distractors with
      // transliterations which may not be met by all content.
      sequenceOptions = _.union(sequenceOptions, ['no_transliteration', 'no_quiz_reverse_multiple_choice_limited']);
    }

    const params = {
      sessionSize:          this._sessionConfig.sessionSize,
      includeMemories:      this._studyAll,
      sequenceOptions,
      supportedQuizTypes:   _.keys(Quizzes),
      applicationType:      this._applicationType,
    };

    if (this._sessionConfig.goalSource) {
      params.sessionGoalSource = this._sessionConfig.goalSource;
    }

    return params;
  }

  /**
   * Callback used to update session changes made on the welcome
   * screen.
   *
   * @param {String} type
   * @param {String|Integer} val
   */
  _handleSessionChange(type, val) {
    this._sessionConfig.update({ [type]: val });

    // Rerender the welcome screen while fetching results.
    this._showWelcomeScreen();

    // Retrieve a new session and persist session config.
    this._fetchData();
    this._persistSessionConfig();
  }

  /**
   * Callback for after presentation results have been sent to the server.
   *
   * @param {Object[]} presentationResults    results sent
   * @param {Object}   sessionResults         response from the server
   */
  _handlePresentationResultsUploaded(presentationResults, sessionResults) {
    this._updatedMemories = [];

    // Store memory changes and apply them to any memories.
    if (sessionResults.memories) {
      sessionResults.memories.forEach(updatedMemory => {
        // itemPackage or sentence.
        let memoryOwner;

        switch (updatedMemory.contentDomain) {
          case 'items':
            memoryOwner = this._sessionData.itemPackages[updatedMemory.contentId];
            break;
          case 'sentences':
            memoryOwner = this._sessionData.sentences[updatedMemory.contentId];
            break;
        }

        if (!memoryOwner.memory) {
          memoryOwner.memory = new Memory(updatedMemory);
        } else {
          memoryOwner.memory.update(updatedMemory);
        }

        this._updatedMemories.push(memoryOwner.memory);
      });
    }

    // Cast all the aggregates to AggregateMemory.
    sessionResults.goalMemoryAggregates = sessionResults.goalMemoryAggregates.map(
      aggregate => new AggregateMemory(aggregate)
    );

    // Session has been uploaded, grab new memories next time data is fetched.
    this._reloadMemories();

    // Determine which screen to show next.
    const showSummaryScreen = this._showSummaryScreen.bind(this, presentationResults, sessionResults);
    const achievements = checkForcedAchievement() || sessionResults.achievements || [];

    if (achievements.length > 0) {
      this._screenManager.addEvent('returned_from_achievements', showSummaryScreen);
      const isSocialSharingEnabled = this._siteConfiguration.features.socialSharing;
      this._screenManager.populateAndShow('achievements', achievements, isSocialSharingEnabled);
    } else {
      showSummaryScreen();
    }
  }

  /**
   * Persists the session configurations.
   *
   * @returns {Object}      the settings to persist
   */
  _persistSessionConfig() {
    const config = _.pick(this._sessionConfig, 'sessionSize', 'itemSource', 'goalSource');
    const appKey = this._getApplicationTypeKey();
    const studyModeKey = this._getStudyModeKey();

    // Update the UserSettings object and get the difference.
    const changes = {
      apps: {
        [appKey]: {
          sessionConfig: {
            [studyModeKey]: config,
          },
        },
      },
    };

    // Persist the user settings object.
    this._userSettings.update(changes);

    // Persist in the API for next time.
    API.postSettings(changes);
  }

  /**
   * Retrieves settings panels for the app.
   *
   * @returns {Array}
   */
  _getSettingsPanels() {
    const panels = [AudioPanel];

    if (this._cueLanguage === 'ja') {
      panels.splice(1, 0, KanaPanel);
    }

    return panels;
  }

  _handleSequencerStarted() {
    super._handleSequencerStarted();

    this._sessionStarted = true;
  }

  /**
   * Callback for when the sequencer finishes.
   *
   * @param {Object[]} presentationResults    generated by sequencer
   */
  _handleSequencerEnded(presentationResults = []) {
    super._handleSequencerEnded();

    this._finishedSessionsCount++;
    this._screenManager.show('loading');

    // Reset the banner.
    this._renderBanner();

    // Sequencer ended without results. Simply show the summary screen.
    if (presentationResults.length === 0) {
      this._showSummaryScreen(presentationResults, null);
    } else {
      this._savePresentationResults(
        presentationResults,
        this._handlePresentationResultsUploaded.bind(this, presentationResults)
      );
    }
  }

  _handleSequencerProgress(progress) {
    this._renderBanner({
      showPauseButton: true,
      showSettingsButton: true,
      children: (<SessionProgressBar progress={progress} />),
    });
  }

  /**
   * Handles the saving of the user settings. The SettingsModal
   * will return an object of the new user settings.
   *
   * @param {UserSettings} userSettings
   */
  _handleSettingsSave(userSettings) {
    // Transfer the values.
    const changes = this._userSettings.update(userSettings);

    // Post the changes to the user settings
    API.postSettings(changes);

    // Update all the fields for legacy purposes.
    this._updateGlobalSettings();

    // If the session hasn't started yet (e.g. during the welcome screen), refetch the data.
    if (!this._sessionStarted) {
      this._fetchData();
    }
  }

  /**
   * Function passed to the ScreenBanner component to render the contents of the settings modal.
   *
   * @param {Function} closeModal a callback that closes the settings modal, which will be passed in
   * by the ScreenBanner component.
   */
  _renderSettingsModalContents(closeModal) {
    return (
      <SettingsModal
        inSession={this._sessionInProgress()}
        panel={AudioPanel}
        panels={this._getSettingsPanels()}
        onSave={(userSettings) => {
          this._handleSettingsSave(userSettings);
          closeModal();
        }}
        userSettings={this._userSettings} />
    );
  }

  /**
   * Will cause the memory loaders to reload memories next time they
   * are called.
   *
   * This function will happen when an item is skipped or a session ends.
   */
  _reloadMemories() {
    if (this._studyAll) {
      Loaders.FocusedAggregateMemoriesLoader.invalidate();
    } else {
      const goalId = this._sessionData.goals[0].id;

      Loaders.AggregateMemoryLoader.invalidate(goalId);
      Loaders.GoalMemoriesLoader.invalidate(goalId);
    }
  }

  /**
   * Handles the basic setting up of a session.
   *
   * @params {Object}    packedData study loader response
   */
  _unpackData(packedData) {
    // Build session data.
    const sessionData = this._buildSessionData(packedData);

    // For study all, the session data grows to include item packages
    // and sentences accumulated from prior sessions. The memory bank
    // and summary screen need to have this information available.
    if (this._studyAll && this._sessionData) {
      const itemPackages = Object.assign({}, this._sessionData.itemPackages, sessionData.itemPackages);
      const sentences = Object.assign({}, this._sessionData.sentences, sessionData.sentences);

      // Put the merged item packages and sentences into the sessionData object.
      Object.assign(sessionData, { itemPackages, sentences });
    }

    // Because the study API does not return the difficulty bucket
    // for memories. We need to re-retrieve the memories. This code
    // ensures any updated memories from the previous session do not
    // change.
    this._updatedMemories.forEach(updatedMemory => {
      let memoryOwner;

      switch (updatedMemory.contentDomain) {
        case 'items':
          memoryOwner = sessionData.itemPackages[updatedMemory.contentId];
          break;
        case 'sentences':
          memoryOwner = sessionData.sentences[updatedMemory.contentId];
          break;
        default:
          return;
      }

      // I'm not really sure I understand what this whole loop is intended to
      // do but for new items without previous memories, `_updatedMemories`
      // will not have the difficultyBucket set and break the UI so we just
      // copy the difficultyBucket from the newly-fetched memories.
      if (updatedMemory.difficultyBucket == null && memoryOwner.memory != null) {
        updatedMemory.difficultyBucket = memoryOwner.memory.difficultyBucket;
      }

      memoryOwner.memory = updatedMemory;
    });

    // Swap out the existing session data with the new one.
    this._sessionData = sessionData;

    // Update session settings based on session data. If a setting is
    // forcibly changed. Start retrieving a new data session and halt this one.
    if (!this._updateSessionConfig()) {
      Logger.info('Force Session Change', 'Needed to get a new session because options are not available.');
      this._fetchData();
      this._haltFetchData();
      return;
    }

    // Override session data if any debugging flags are on.
    checkAllOverrides(this._sessionData);

    // Load assets in background.
    const goals = _.pluck(this._sessionData.goals, 'id');
    Promise.all(_.map(goals, (goal) => { new Loaders.GoalAssets(goal); }));

    // Create an asset loader for the session.
    this._assetLoader = this._loadSessionAssets();
  }

  /**
   * Used to handle quitting a session. If the user is the middle
   * of a session, try to save progress and then redirect.
   */
  _quitSession() {
    // If the session is in progress,
    if (this._sessionInProgress()) {
      this._screenManager.show('loading');
      this._forceFinishSession().then(() => super._quitSession());
    } else {
      super._quitSession();
    }
  }

  /**
   * Forces the sequencer to finish the session early, either sending
   * the existing data to the server or storing locally to try to send
   * later.
   *
   * @param {Boolean} storeLocally  if the data should be stored locally and sent later
   * @returns {Promise}
   */
  _forceFinishSession(storeLocally) {
    if (!this._sessionInProgress()) {
      return Promise.resolve(true);
    }

    // TODO: Look into making forceFinishSequence return a promise.
    return new Promise((resolve, reject) => {
      this._sequencer.forceFinishSequence(presentationResults => {
        if (storeLocally) {
          StudyDataManager.storePresentationData(presentationResults, this._applicationType);
          resolve(true);
        } else {
          this._savePresentationResults(presentationResults, resolve);
        }
      });
    });
  }

  /**
   * The app settings pass a callback function for when the user wants
   * to go and choose another app.
   *
   * This is essentially ending the apps lifecycle. Unbind as much as
   * possible.
   *
   * @param {String}  appPath         specify if to redirect to another app
   * @param {Boolean} isCheckpoint    only for sentence trainer, will pass it
   */
  _quitApplication(appPath = null, isCheckpoint = false) {
    // Unbind screen events.
    this._screenManager.removeAll();
    this._screenManager.hide();

    // Unbind application events.
    this._unbindEvents();

    // Hit the callback passed to the app.
    this._onAppQuit(appPath, isCheckpoint);
  }

  /**
   * This method takes the user settings and puts them onto
   * the global namespace. This so legacy parts of the app
   * can continue to work.
   *
   * TODO: Try to eliminate as many of these globals as possible.
   */
  _updateGlobalSettings() {
    const appSettings = this._userSettings.apps;

    $.setVolume(appSettings.contentVolume * 10);
    Smartfm.Apps.Settings.EffectVolume = appSettings.effectVolume * 10;
    Smartfm.Apps.Settings.SpecialCharacters = appSettings.typingSpecialCharacters;
    Smartfm.Apps.Settings.TypingSound = appSettings.typingSound;

    // Update the config settings.
    Config.updateSettings(this._userSettings, { setKana: this._cueLanguage === 'ja' });
  }
}
